import { format } from 'date-fns'
import { maxBy } from 'lodash'

import { InstrumentBookFullDto, useGetInstrumentBookFull } from '@/gen/marketDataApi'
import { InstrumentDto } from '@/gen/publicApi'

import { UnexpectedDataStateError } from '@/containers/unexpectedDataStateError/index.tsx'

import { Card, CardContent } from '@/components/card'
import Spinner from '@/components/spinner'

import Asks from './asks.tsx'
import Bids from './bids.tsx'

interface Props {
    clientCode: string
    marketCode: string
    instrument: InstrumentDto
}

type OrderVolumes = InstrumentBookFullDto['bids'][0]['orderVolumes'] | InstrumentBookFullDto['asks'][0]['orderVolumes']
const sumOrderVolmues = (orderVolumes: OrderVolumes) => {
    return orderVolumes.reduce((acc, v) => acc + parseInt(v), 0)
}

export default function InstrumentFull({ clientCode, marketCode, instrument }: Props) {
    const full = useGetInstrumentBookFull(clientCode, marketCode, instrument.symbol, {
        query: { refetchInterval: 1000 },
    })
    if (full.isLoading) {
        return <Spinner size="sm" absolute navVisible />
    }

    if (!full.data) {
        return <UnexpectedDataStateError />
    }

    const currency = (str?: string | null) => {
        return str ? `$${str}` : '--'
    }

    // TODO - should this be reversed when we get it from the server?
    const bids = full.data.bids
        .sort((a, b) => parseFloat(b.price) - parseFloat(a.price))
        .map(point => {
            const quantity = sumOrderVolmues(point.orderVolumes)
            return {
                ...point,
                quantity,
            }
        })
    const asks = full.data.asks.map(point => {
        const quantity = sumOrderVolmues(point.orderVolumes)
        return {
            ...point,
            quantity,
        }
    })

    const largestBidOrAskQuantity = maxBy([...bids, ...asks], 'quantity')?.quantity || 0

    return (
        <>
            <div className="grid grid-cols-4 gap-4 mb-4">
                <Card>
                    <CardContent className="pt-6">
                        <h3>Total Value</h3>
                        <h5>{currency(full.data.totalValue)}</h5>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <h3>Total Volume</h3>
                        <h5>{full.data.totalVolume || '--'}</h5>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <h3>Last Trade</h3>
                        <h5>{currency(full.data.lastTraded)}</h5>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <h3>Last Trade At</h3>
                        <h5>{full.data.lastTradedAt ? format(full.data.lastTradedAt, 'HH:mm:ss') : '--'}</h5>
                    </CardContent>
                </Card>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <Bids bids={bids} largestQuantity={largestBidOrAskQuantity} otherSideCount={asks.length} />
                <Asks asks={asks} largestQuantity={largestBidOrAskQuantity} otherSideCount={bids.length} />
            </div>
        </>
    )
}
